import React, { useState, useRef } from "react";
import { Upload, FileUp, Download, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";

const SAMPLE = `Title, Date (YYYY-MM-DD), Start (e.g. 3:30 PM), End (e.g. 5:00 PM), Location, Description, Group
Team Practice, 2026-08-20, 3:30 PM, 5:00 PM, Main Gym, Bring water, Varsity
Film Study, 2026-08-22, , , Room 210, Optional review, JV
Season Retreat, 2026-08-25, , , Camp Lodge, All-day kickoff, `;

export function BulkImportDialog({ onDone }) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState([]);
  const fileRef = useRef(null);

  const downloadTemplate = () => {
    const blob = new Blob([SAMPLE + "\n"], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "rallypointgo_schedule_template.csv";
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const content = await file.text();
    setText(content);
    setErrors([]);
    toast.success(`Loaded ${file.name}`);
  };

  const submit = async () => {
    if (!text.trim()) { toast.error("Paste rows or upload a CSV first"); return; }
    setLoading(true);
    setErrors([]);
    try {
      const { data } = await api.post("/coach/events/bulk", { csv: text });
      if (data.created > 0) {
        toast.success(`Imported ${data.created} event${data.created === 1 ? "" : "s"} quietly — no notifications sent`);
        onDone && onDone();
      } else {
        toast.error("No events were imported — check the rows below");
      }
      setErrors(data.errors || []);
      if (data.created > 0 && (!data.errors || data.errors.length === 0)) {
        setOpen(false);
        setText("");
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (o) { setErrors([]); } }}>
      <Button variant="outline" className="rounded-full" onClick={() => setOpen(true)} data-testid="bulk-import-open-btn">
        <Upload className="mr-2 h-4 w-4" /> Import schedule
      </Button>
      <DialogContent className="max-h-[90dvh] overflow-y-auto" data-testid="bulk-import-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Import season schedule</DialogTitle>
          <DialogDescription>
            Paste rows or upload a CSV — one event per line. Imported events are saved and synced to Google Calendar
            <strong> silently</strong> (no push/email to the team).
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <input ref={fileRef} type="file" accept=".csv,text/csv,text/plain" onChange={onFile} className="hidden" data-testid="bulk-import-file-input" />

          <ol className="space-y-2.5 text-sm text-slate-600" data-testid="bulk-import-steps">
            <li className="flex flex-wrap items-center gap-1.5">
              <span className="font-semibold text-slate-800">Step 1:</span>
              <span>Click</span>
              <Button type="button" variant="outline" size="sm" className="rounded-full" onClick={downloadTemplate} data-testid="bulk-import-template-btn">
                <Download className="mr-1.5 h-3.5 w-3.5" /> Download Template
              </Button>
              <span>to get the template file.</span>
            </li>
            <li>
              <span className="font-semibold text-slate-800">Step 2:</span> Open the template and delete the example entries, keeping the header row.
            </li>
            <li>
              <span className="font-semibold text-slate-800">Step 3:</span> Fill in each row with your event details. Time details are optional and can be skipped. Leave the group detail blank if the event is for the entire team.
            </li>
            <li className="flex flex-wrap items-center gap-1.5">
              <span className="font-semibold text-slate-800">Step 4:</span>
              <span>Save the file as a .csv and click</span>
              <Button type="button" variant="outline" size="sm" className="rounded-full" onClick={() => fileRef.current?.click()} data-testid="bulk-import-upload-btn">
                <FileUp className="mr-1.5 h-3.5 w-3.5" /> Upload
              </Button>
              <span>to import your schedule.</span>
            </li>
          </ol>

          <div className="rounded-lg border border-slate-200 bg-slate-50 p-3" data-testid="bulk-import-pasting-instructions">
            <p className="mb-1.5 text-xs font-bold uppercase tracking-wide text-slate-500">Pasting Instructions</p>
            <ul className="list-disc space-y-1 pl-5 text-xs text-slate-600">
              <li>Place a comma between each detail.</li>
              <li>Use two consecutive commas for optional fields you want to skip (start time, end time, and group).</li>
              <li>Leave the group field blank if the event is for the entire team.</li>
            </ul>
          </div>

          <div className="overflow-x-auto rounded-md border border-dashed border-slate-200 bg-slate-50/60 px-3 py-2" data-testid="bulk-import-sample-row">
            <p className="mb-0.5 text-[10px] font-semibold uppercase tracking-wide text-slate-400">Example</p>
            <p className="whitespace-nowrap font-mono text-xs text-slate-400">Team Practice, 2026-08-20, 3:30 PM, 5:00 PM, Main Gym, Bring water, Varsity</p>
          </div>

          <Textarea rows={9} value={text} onChange={(e) => setText(e.target.value)} placeholder="…or paste your rows here" className="font-mono text-xs" data-testid="bulk-import-textarea" />
          {errors.length > 0 && (
            <div className="max-h-32 overflow-y-auto rounded-lg border border-amber-200 bg-amber-50 p-3" data-testid="bulk-import-errors">
              <p className="mb-1 text-xs font-semibold text-amber-800">{errors.length} row{errors.length === 1 ? "" : "s"} skipped:</p>
              <ul className="space-y-0.5 text-xs text-amber-700">
                {errors.map((er, i) => <li key={i}>• {er}</li>)}
              </ul>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button onClick={submit} disabled={loading} className="rounded-full bg-slate-900 hover:bg-slate-800" data-testid="bulk-import-submit-btn">
            {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Import events
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default BulkImportDialog;
